import axios from 'axios';
import connectionService from './ConnectionService';
import AuthService from './AuthService';
import ApiConfig from './ApiConfig';

const PRODUCTS_KEY = 'pos-products';
const SESSION_KEY = 'pos-session';
const API_CACHE_PREFIX = 'pos-cache-';

const OfflineService = {
    isOnline() {
        return connectionService.getConnectionStatus();
    },

    saveSessionInfo(sessionData, configId, loginDate) {
        const cleanedSessionData = {
            ...sessionData,
            products: sessionData.products?.map(product => ({
                ...product,
                image_base64: null
            }))
        };

        localStorage.setItem(
            SESSION_KEY,
            JSON.stringify({
                sessionData: cleanedSessionData,
                configId: configId,
                loginDate: loginDate,
                timestamp: Date.now(),
            })
        );
    },

    getSessionInfo() {
        const sessionInfo = localStorage.getItem(SESSION_KEY);
        if (!sessionInfo) return { sessionData: null, configId: null, loginDate: null };

        try {
            const data = JSON.parse(sessionInfo);
            return {
                sessionData: data.sessionData,
                configId: data.configId,
                loginDate: data.loginDate,
            };
        } catch (error) {
            console.error('Error al parsear la informacion de sesion:', error);
            return { sessionData: null, configId: null, loginDate: null };
        }
    },

    clearSessionInfo() {
        localStorage.removeItem(SESSION_KEY);
    },

    saveProducts(products) {
        const cleanedProducts = products?.map(product => ({
            ...product,
            image_base64: null
        }));

        localStorage.setItem(
            PRODUCTS_KEY,
            JSON.stringify({
                products: cleanedProducts,
                timestamp: Date.now(),
            })
        );
    },

    getProducts() {
        const productsJson = localStorage.getItem(PRODUCTS_KEY);
        if (!productsJson) return [];

        try {
            const data = JSON.parse(productsJson);
            return data.products || [];
        } catch (error) {
            console.error('Error al parsear productos:', error);
            return [];
        }
    },

    clearProducts() {
        localStorage.removeItem(PRODUCTS_KEY);
    },

    cacheApiResponse(url, data) {
        const cacheKey = `${API_CACHE_PREFIX}${url}`;
        localStorage.setItem(
            cacheKey,
            JSON.stringify({
                data: data,
                timestamp: Date.now(),
            })
        );
    },

    getCachedApiResponse(url) {
        const cacheKey = `${API_CACHE_PREFIX}${url}`;
        const cachedJson = localStorage.getItem(cacheKey);

        if (!cachedJson) return null;

        try {
            const cached = JSON.parse(cachedJson);
            const isRecent = Date.now() - cached.timestamp < 24 * 60 * 60 * 1000;
            return isRecent ? cached.data : null;
        } catch (error) {
            console.error('Error al parsear cache de API:', error);
            return null;
        }
    },

    clearCachedApiResponse(url) {
        const cacheKey = `${API_CACHE_PREFIX}${url}`;
        localStorage.removeItem(cacheKey);
    },

    async validateSession(pin) {
        console.log('Validando sesion con PIN...');

        const user = AuthService.getUser();
        const userId = user ? user.user_id : null;

        if (!userId) {
            throw new Error('No hay usuario autenticado');
        }

        console.log('Usando usuario autenticado ID:', userId);

        try {
            console.log('Enviando validacion a Odoo...');

            const response = await axios.post(
                `${ApiConfig.getBaseUrl()}/pos_validate_session`,
                {
                    pin: pin,
                    user_id: userId
                },
                {
                    timeout: 180000,
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );

            const result = response.data;

            if (result && result.authenticated) {
                console.log('Sesion validada exitosamente');
                console.log('POS encontrado:', result.config_name);
                console.log('Session ID:', result.session_id);
                return result;
            } else {
                throw new Error(result?.error || 'Error en validacion de sesion');
            }

        } catch (error) {
            console.error('Error validando sesion:', error.message);

            if (error.response) {
                const errorMsg = error.response.data?.error || error.response.statusText;
                if (error.response.status === 401) {
                    throw new Error('PIN incorrecto o POS no configurado');
                } else if (error.response.status === 500) {
                    throw new Error('Error del servidor: ' + errorMsg);
                } else {
                    throw new Error(errorMsg || 'Error en validacion');
                }
            } else {
                throw new Error('Error de conectividad con el servidor');
            }
        }
    },

    async closeSession(sessionId, pin, confirm = false) {
        console.log(`${confirm ? 'Cerrando' : 'Obteniendo datos de'} sesion...`);
        console.log('Parametros:', { sessionId, pin, confirm });

        const user = AuthService.getUser();
        const userId = user ? user.user_id : null;

        if (userId) {
            console.log('Usando usuario autenticado ID:', userId);
        }

        try {
            console.log('Enviando operacion a Odoo...');

            const formData = new URLSearchParams();
            formData.append('session_id', sessionId.toString());
            formData.append('pin', pin);
            formData.append('confirm', confirm ? 'true' : 'false');
            if (userId) {
                formData.append('user_id', userId.toString());
            }

            const response = await axios.post(
                `${ApiConfig.getBaseUrl()}/pos_close_session`,
                formData,
                {
                    timeout: 180000,
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    }
                }
            );

            const result = response.data;

            if (result && result.success) {
                console.log(`${confirm ? 'Sesion cerrada' : 'Datos obtenidos'} exitosamente`);
                return result;
            } else {
                throw new Error(result?.error || 'Error en operacion de sesion');
            }

        } catch (error) {
            console.error('Error en operacion:', error.message);

            if (error.response && error.response.data) {
                throw new Error(error.response.data.error || 'Error en el servidor');
            } else {
                throw new Error('Error de conectividad con el servidor');
            }
        }
    }
};

export default OfflineService;